import React, { useRef, useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Inputmask from "inputmask";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import { MdClear } from "react-icons/md";

interface PriceValues {
    priceMin: string;
    priceMax: string;
}

interface PriceRangeInputProps {
    values?: PriceValues;
    onChange: (field: keyof PriceValues, value: string) => void;
}

const INPUTMASK_CONFIG = {
    alias: "numeric",
    allowMinus: false,
    rightAlign: false,
    groupSeparator: ",",
    autoGroup: true,
    digits: 0,
    radixPoint: "",
    prefix: "$",
    positionCaretOnClick: "none",
    max: "999999999999999",
    onBeforePaste: function (pastedValue: string) {
        return pastedValue.replace(/[^\d]/g, "");
    },
    onBeforeWrite: function (event: any, buffer: string[], caretPos: any, opts?: any) {
        const { type } = event;
        if (type !== "checkval" || opts?.skip) {
            return;
        }
        const isLast3DigitsZero = buffer.slice(0, 3).every((item: string) => item === "0");
        const newCaretPos = buffer.length <= 4 ? 3 : 4;
        return { caret: isLast3DigitsZero && buffer.length >= 4 ? newCaretPos : caretPos };
    },
    onKeyDown: function (event: any, buffer: string[], caretPos: any, opts: any) {
        opts.skip = event.key === "Backspace" && caretPos?.end === 0;
    },
};

const PriceRangeInput = ({ values = { priceMin: "", priceMax: "" }, onChange }: PriceRangeInputProps) => {
    const priceMinRef = useRef<HTMLInputElement>(null);
    const priceMaxRef = useRef<HTMLInputElement>(null);
    const [dirtyMin, setDirtyMin] = useState(false);
    const [dirtyMax, setDirtyMax] = useState(false);
    const [touchedMin, setTouchedMin] = useState(false);
    const [touchedMax, setTouchedMax] = useState(false);
    const [focusedField, setFocusedField] = useState<string | null>(null);

    useEffect(() => {
        const inputMask = Inputmask(INPUTMASK_CONFIG as any);
        inputMask.mask("feed-price-min");
        inputMask.mask("feed-price-max");
    }, []);

    const handlePriceInput = (
        val: string,
        setValue: (value: string) => void,
        isDirty: boolean,
        setDirty: (dirty: boolean) => void,
    ) => {
        const cleanValue = val?.replace(/[^\d]/g, "") || "";

        if (!cleanValue) {
            setValue("");
            setDirty(false);
            return;
        }

        // If the input is a single digit and not dirty, add three zeros
        if (cleanValue.length === 1 && !isDirty) {
            setValue(cleanValue + "000");
            setTimeout(() => {
                const ref = cleanValue === values.priceMin ? priceMinRef : priceMaxRef;
                if (ref && ref.current) {
                    ref.current.setSelectionRange(1, 1);
                }
            }, 0);
            return;
        }

        setValue(cleanValue);
        setDirty(true);
    };

    const handleFocus = (
        ref: React.RefObject<HTMLInputElement | null>,
        value: string,
        setValue: (value: string) => void,
        setDirty: (dirty: boolean) => void,
        field: string,
    ) => {
        setFocusedField(field);
        const cleanValue = value?.replace(/[^\d]/g, "") || "";
        if (!cleanValue) {
            setValue("000");
            setDirty(false);
            setTimeout(() => {
                if (ref && ref.current) {
                    ref.current.setSelectionRange(0, 0);
                }
            }, 0);
        }
    };

    const handleBlur = (field: string) => {
        setFocusedField(null);
        if (field === "priceMin") {
            if (values.priceMin === "000") onChange("priceMin", "");
            setTouchedMin(true);
            setTouchedMax(true);
        } else {
            if (values.priceMax === "000") onChange("priceMax", "");
            setTouchedMax(true);
            setTouchedMin(true);
        }
    };

    const isInvalid =
        (touchedMin || touchedMax) &&
        focusedField === null &&
        Number(values.priceMax) > 0 &&
        Number(values.priceMin) > 0 &&
        Number(values.priceMax) < Number(values.priceMin);

    return (
        <div>
            <label className="tw-block tw-mb-1 tw-text-[14px] tw-text-[#555E67]">Price</label>
            <div className="tw-flex tw-gap-4">
                {/* Min */}
                <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center">
                    <TextField
                        inputRef={priceMinRef}
                        id="feed-price-min"
                        type="text"
                        value={values.priceMin || ""}
                        onChange={(e) =>
                            handlePriceInput(
                                e.target.value,
                                (v: string) => onChange("priceMin", v),
                                dirtyMin,
                                setDirtyMin,
                            )
                        }
                        onFocus={() =>
                            handleFocus(
                                priceMinRef,
                                values.priceMin,
                                (v: string) => onChange("priceMin", v),
                                setDirtyMin,
                                "priceMin",
                            )
                        }
                        onClick={() =>
                            handleFocus(
                                priceMinRef,
                                values.priceMin,
                                (v: string) => onChange("priceMin", v),
                                setDirtyMin,
                                "priceMin",
                            )
                        }
                        onBlur={() => handleBlur("priceMin")}
                        placeholder="$0"
                        size="small"
                        fullWidth
                        error={isInvalid}
                        inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                        }}
                        InputProps={{
                            className: "!tw-px-2",
                            sx: {
                                borderRadius: 2,
                                textAlign: "center",
                                "&.Mui-error": {
                                    color: "red",
                                },
                            },
                            inputProps: {
                                style: {
                                    textAlign: "center",
                                    color: isInvalid ? "red" : "inherit",
                                },
                            },
                            endAdornment: values.priceMin ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => onChange("priceMin", "")}
                                        edge="end"
                                        sx={{ padding: "2px", marginLeft: "2px" }}
                                    >
                                        <MdClear />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <span className="tw-mt-1 tw-text-xs tw-text-gray-400 tw-text-center">min</span>
                </div>
                {/* Max */}
                <div className="tw-flex-1 tw-flex tw-flex-col tw-items-center">
                    <TextField
                        inputRef={priceMaxRef}
                        id="feed-price-max"
                        type="text"
                        value={values.priceMax || ""}
                        onChange={(e) =>
                            handlePriceInput(
                                e.target.value,
                                (v: string) => onChange("priceMax", v),
                                dirtyMax,
                                setDirtyMax,
                            )
                        }
                        onFocus={() =>
                            handleFocus(
                                priceMaxRef,
                                values.priceMax,
                                (v: string) => onChange("priceMax", v),
                                setDirtyMax,
                                "priceMax",
                            )
                        }
                        onClick={() =>
                            handleFocus(
                                priceMaxRef,
                                values.priceMax,
                                (v: string) => onChange("priceMax", v),
                                setDirtyMax,
                                "priceMax",
                            )
                        }
                        onBlur={() => handleBlur("priceMax")}
                        placeholder="$0"
                        size="small"
                        fullWidth
                        error={isInvalid}
                        inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                        }}
                        InputProps={{
                            className: "!tw-px-2",
                            sx: {
                                borderRadius: 2,
                                textAlign: "center",
                                "&.Mui-error": {
                                    color: "red",
                                },
                            },
                            inputProps: {
                                style: {
                                    textAlign: "center",
                                    color: isInvalid ? "red" : "inherit",
                                },
                            },
                            endAdornment: values.priceMax ? (
                                <InputAdornment position="end">
                                    <IconButton
                                        size="small"
                                        onClick={() => onChange("priceMax", "")}
                                        edge="end"
                                        sx={{ padding: "2px", marginLeft: "2px" }}
                                    >
                                        <MdClear />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                        }}
                    />
                    <span className="tw-mt-1 tw-text-xs tw-text-gray-400 tw-text-center">max</span>
                </div>
            </div>
        </div>
    );
};

export default PriceRangeInput;
